import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Loader2, MapPin, User, Phone, Mail, Clock,
  CheckCircle, AlertTriangle, Wrench, PoundSterling, Send,
  Calendar, FileText, MessageSquare
} from "lucide-react";
import { API } from "@/App";

export default function ContractorJobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const [loading, setLoading] = useState(true);
  const [job, setJob] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [declining, setDeclining] = useState(false);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Quote form
  const [quoteAmount, setQuoteAmount] = useState("");
  const [quoteDescription, setQuoteDescription] = useState("");
  const [estimatedDays, setEstimatedDays] = useState("");

  useEffect(() => {
    fetchJobDetails();
    fetchMessages();
  }, [jobId]);

  const fetchJobDetails = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Handle different response formats
      const jobData = response.data.job || response.data;
      setJob(jobData);
    } catch (error) {
      toast.error("Failed to load job details");
      navigate("/contractor");
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await axios.get(`${API}/jobs/${jobId}/messages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data.messages || []);
    } catch (error) {
      console.error("Error fetching messages:", error);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      await axios.post(`${API}/maintenance/jobs/${jobId}/accept`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job accepted! You can now contact the tenant.");
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to accept job");
    } finally {
      setAccepting(false);
    }
  };

  const handleDecline = async () => {
    setDeclining(true);
    try {
      await axios.post(`${API}/maintenance/jobs/${jobId}/decline`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Job declined. It will be offered to another contractor.");
      navigate("/contractor");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline job");
    } finally {
      setDeclining(false);
    }
  };

  const handleSubmitQuote = async () => {
    if (!quoteAmount) {
      toast.error("Please enter a quote amount");
      return;
    }
    
    setSubmittingQuote(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/submit-quote`, {
        amount: parseFloat(quoteAmount),
        description: quoteDescription,
        estimated_days: estimatedDays ? parseInt(estimatedDays) : null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Quote submitted successfully!");
      fetchJobDetails();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit quote");
    } finally {
      setSubmittingQuote(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) return;
    
    setSendingMessage(true);
    try {
      await axios.post(`${API}/jobs/${jobId}/messages`, {
        job_id: jobId,
        content: newMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNewMessage("");
      fetchMessages();
      toast.success("Message sent");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSendingMessage(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      reported: { label: "New", color: "bg-blue-100 text-blue-700", icon: AlertTriangle },
      awaiting_contractor: { label: "Awaiting Response", color: "bg-amber-100 text-amber-700", icon: Clock },
      pending: { label: "Pending Acceptance", color: "bg-amber-100 text-amber-700", icon: Clock },
      accepted: { label: "Accepted", color: "bg-green-100 text-green-700", icon: CheckCircle },
      quote_submitted: { label: "Quote Submitted", color: "bg-purple-100 text-purple-700", icon: FileText },
      quote_required: { label: "Quote Required", color: "bg-orange-100 text-orange-700", icon: PoundSterling },
      in_progress: { label: "In Progress", color: "bg-blue-100 text-blue-700", icon: Wrench },
      completed: { label: "Completed", color: "bg-green-100 text-green-700", icon: CheckCircle }
    };
    
    const config = statusConfig[status] || { label: status, color: "bg-slate-100 text-slate-700", icon: Clock };
    const Icon = config.icon;
    
    return (
      <Badge className={config.color}>
        <Icon className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      urgent: "bg-red-100 text-red-700",
      high: "bg-orange-100 text-orange-700",
      medium: "bg-yellow-100 text-yellow-700",
      low: "bg-green-100 text-green-700"
    };
    return <Badge className={colors[priority] || "bg-slate-100"}>{priority?.toUpperCase()}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="p-6 text-center">
        <p>Job not found</p>
        <Button onClick={() => navigate("/contractor")} className="mt-4">
          Back to Dashboard
        </Button>
      </div>
    );
  }

  const canAccept = ["reported", "awaiting_contractor", "pending"].includes(job.status);
  const needsQuote = job.status === "accepted" || job.status === "quote_required";

  return (
    <div className="min-h-screen bg-slate-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate("/contractor")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold">{job.title || "Maintenance Job"}</h1>
            <p className="text-slate-500">Job #{jobId.slice(-8).toUpperCase()}</p>
          </div>
          <div className="flex gap-2">
            {getStatusBadge(job.status)}
            {job.priority && getPriorityBadge(job.priority)}
          </div>
        </div>

        {/* Property & Tenant Info */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Job Location
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="font-semibold text-lg">{job.property_address || "Address not provided"}</p>
              {job.property_postcode && (
                <p className="text-slate-500">{job.property_postcode}</p>
              )}
            </div>
            
            {(job.tenant_name || job.tenant_email || job.tenant_phone) && (
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Tenant Contact
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {job.tenant_name && (
                    <div className="flex items-center gap-2">
                      <User className="w-4 h-4 text-slate-400" />
                      <span>{job.tenant_name}</span>
                    </div>
                  )}
                  {job.tenant_email && (
                    <a href={`mailto:${job.tenant_email}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                      <Mail className="w-4 h-4" />
                      {job.tenant_email}
                    </a>
                  )}
                  {job.tenant_phone && (
                    <a href={`tel:${job.tenant_phone}`} className="flex items-center gap-2 text-blue-600 hover:underline">
                      <Phone className="w-4 h-4" />
                      {job.tenant_phone}
                    </a>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Job Details */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5" />
              Job Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-slate-500">Category</Label>
                <p className="font-medium">{job.category || "General"}</p>
              </div>
              <div>
                <Label className="text-slate-500">Reported</Label>
                <p className="font-medium">{new Date(job.created_at).toLocaleDateString()}</p>
              </div>
            </div>
            
            <div>
              <Label className="text-slate-500">Description</Label>
              <p className="mt-1 bg-slate-50 p-4 rounded-lg whitespace-pre-wrap">
                {job.description || "No description provided"}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons for New Jobs */}
        {canAccept && (
          <Card className="border-purple-200 bg-purple-50">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-semibold text-purple-900">Accept this job?</h3>
                  <p className="text-purple-700 text-sm">
                    By accepting, you agree to complete this work and will need to submit a quote.
                  </p>
                </div>
                <div className="flex gap-3">
                  <Button 
                    variant="outline" 
                    onClick={handleDecline}
                    disabled={declining}
                  >
                    {declining ? <Loader2 className="w-4 h-4 animate-spin" /> : "Decline"}
                  </Button>
                  <Button 
                    onClick={handleAccept}
                    disabled={accepting}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    {accepting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Accepting...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Accept Job
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Quote Submission for Accepted Jobs */}
        {needsQuote && !job.quote_submitted && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <PoundSterling className="w-5 h-5" />
                Submit Your Quote
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="amount">Quote Amount (£)</Label>
                  <Input
                    id="amount"
                    type="number"
                    placeholder="e.g. 150"
                    value={quoteAmount}
                    onChange={(e) => setQuoteAmount(e.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="days">Estimated Days</Label>
                  <Input
                    id="days"
                    type="number"
                    placeholder="e.g. 2"
                    value={estimatedDays}
                    onChange={(e) => setEstimatedDays(e.target.value)}
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="description">Description of Work</Label>
                <Textarea
                  id="description"
                  placeholder="Describe the work you'll do..."
                  value={quoteDescription}
                  onChange={(e) => setQuoteDescription(e.target.value)}
                  className="min-h-[100px]"
                />
              </div>
              <Button 
                onClick={handleSubmitQuote}
                disabled={submittingQuote || !quoteAmount}
                className="w-full"
              >
                {submittingQuote ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Quote
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Quote Details if Submitted */}
        {job.quote && (
          <Card className="border-green-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-green-700">
                <CheckCircle className="w-5 h-5" />
                Quote Submitted
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500">Amount</Label>
                  <p className="font-semibold text-lg">£{job.quote.amount?.toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-slate-500">Status</Label>
                  <Badge className={job.quote.approved ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700"}>
                    {job.quote.approved ? "Approved" : "Pending Approval"}
                  </Badge>
                </div>
              </div>
              {job.quote.description && (
                <div className="mt-4">
                  <Label className="text-slate-500">Description</Label>
                  <p className="mt-1">{job.quote.description}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Messages */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Messages
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="max-h-64 overflow-y-auto space-y-3">
              {messages.length === 0 ? (
                <p className="text-slate-500 text-center py-4">No messages yet</p>
              ) : (
                messages.map((msg, idx) => (
                  <div 
                    key={idx} 
                    className={`p-3 rounded-lg ${
                      msg.sender_type === "contractor" 
                        ? "bg-purple-50 ml-8" 
                        : "bg-slate-50 mr-8"
                    }`}
                  >
                    <div className="flex justify-between text-sm text-slate-500 mb-1">
                      <span className="font-medium">{msg.sender_name || "Unknown"}</span>
                      <span>{msg.created_at ? new Date(msg.created_at).toLocaleString() : msg.timestamp ? new Date(msg.timestamp).toLocaleString() : ""}</span>
                    </div>
                    <p>{msg.message || msg.content}</p>
                  </div>
                ))
              )}
            </div>
            
            <div className="flex gap-2 pt-4 border-t">
              <Textarea
                placeholder="Type a message..."
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                className="min-h-[60px]"
              />
              <Button 
                onClick={handleSendMessage}
                disabled={sendingMessage || !newMessage.trim()}
              >
                {sendingMessage ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
